<template>
  <div class="page-container">

    <!-- Header -->
    <div class="page-title-container d-flex align-items-center justify-content-between flex-wrap">
      <h1 class="page-title mb-0">Control de asistencia</h1>
      <div class="month-nav d-flex align-items-center mt-2 mt-md-0">
        <button type="button" class="month-btn" @click="prevMonth">
          <i class="ri-arrow-left-s-line"></i>
        </button>
        <span class="month-label mx-3">{{ monthLabel }}</span>
        <button type="button" class="month-btn" @click="nextMonth">
          <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

    <!-- Filtros + acciones -->
    <div class="d-flex flex-wrap align-items-end justify-content-between mb-3">
      <div class="d-flex flex-wrap align-items-center grid-filters">
        <div class="filter-field mr-3 mb-2">
          <label class="label-filter mb-1">Empresa</label>
          <SearchableDropdown
            :options="companies"
            :oldValue="filters.companyId"
            placeholder="Todas las empresas"
            @selected="onCompanySelected"
          />
        </div>
        <div class="filter-field mr-3 mb-2">
          <label class="label-filter mb-1">Área</label>
          <SearchableDropdown
            :options="areas"
            :oldValue="filters.areaId"
            placeholder="Todas las áreas"
            @selected="onAreaSelected"
          />
        </div>
        <div class="mb-2">
          <button type="button" class="button button-light button-sm" @click="clearFilters">
            <i class="ri-refresh-line mr-1"></i>Limpiar
          </button>
        </div>
      </div>
      <div class="d-flex mb-2">
        <button type="button" class="button button-light button-sm mr-2"
          @click="downloadTemplate" :disabled="downloadingTemplate">
          <i class="ri-download-line mr-1"></i>
          <span v-if="downloadingTemplate">Descargando...</span>
          <span v-else>Descargar plantilla</span>
        </button>
        <button type="button" class="button button-primary button-sm"
          @click="uploadModalVisible = true">
          <i class="ri-upload-line mr-1"></i>Cargar Excel
        </button>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="!loading && rows.length === 0" class="text-center text-muted py-5">
      <i class="ri-time-line" style="font-size: 3rem; opacity: 0.35;"></i>
      <p class="mt-3 h5">No hay registros de asistencia para este mes</p>
      <p class="small">Intenta con otro mes o revisa los filtros aplicados.</p>
    </div>

    <!-- Grilla -->
    <div v-else class="attendance-outer">
      <div class="attendance-wrapper">

        <!-- Header row: nombre + días -->
        <div class="att-row att-header" :style="gridStyle">
          <div class="att-col-name att-header-name">Empleado</div>
          <div
            v-for="d in dayNumbers"
            :key="'h-' + d"
            class="att-header-day"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
          >
            <div class="day-number">{{ d }}</div>
            <div class="day-letter">{{ dayLetter(d) }}</div>
          </div>
        </div>

        <!-- Filas de empleados -->
        <div
          v-for="(row, rowIdx) in rows"
          :key="rowIdx"
          class="att-row att-data-row"
          :style="gridStyle"
        >
          <!-- Nombre (sticky) -->
          <div class="att-col-name" :title="row.employeeName">
            <div class="emp-name">{{ row.employeeName }}</div>
            <div class="emp-doc">{{ row.employeeIdentifier }}</div>
          </div>

          <!-- Celda por día -->
          <div
            v-for="d in dayNumbers"
            :key="'c-' + d"
            class="att-day-cell"
            :class="{ weekend: isWeekend(d), today: isToday(d) }"
            @click="openCell(row, d)"
          >
            <div
              v-if="getDay(row, d)"
              class="att-cell-badge"
              :class="{ 'auto-detected': getDay(row, d).isAutoDetected, 'has-tardiness': getDay(row, d).tardinessMinutes > 0 }"
              :style="{ backgroundColor: statusColor(getDay(row, d).status) }"
              :title="cellTooltip(getDay(row, d))"
            >
              {{ statusLetter(getDay(row, d).status) }}
            </div>
          </div>
        </div>

      </div>

      <!-- Leyenda -->
      <div class="att-legend d-flex flex-wrap align-items-center mt-3">
        <div v-for="s in statusList" :key="s.key" class="legend-item mr-3 mb-1">
          <span class="legend-dot" :style="{ backgroundColor: s.color }"></span>
          <span class="small ml-1">{{ s.label }}</span>
        </div>
        <div class="legend-item mr-3 mb-1">
          <span class="legend-dot tardiness-dot"></span>
          <span class="small ml-1">Con tardanza</span>
        </div>
        <div class="legend-item mb-1">
          <span class="legend-dot auto-dot"></span>
          <span class="small ml-1">Auto-detectado</span>
        </div>
      </div>
    </div>

    <!-- Modal de edición de celda -->
    <AttendanceEditModal
      v-if="selectedCell"
      :visible="modalVisible"
      :employee-name="selectedCell.employeeName"
      :employee-id="selectedCell.employeeId"
      :record-date="selectedCell.recordDate"
      :day-data="selectedCell.day"
      @saved="onSaved"
      @close="modalVisible = false"
    />

    <!-- Modal de carga de Excel -->
    <b-modal
      v-model="uploadModalVisible"
      title="Cargar Excel de asistencia"
      size="md"
      centered
      hide-footer
      @hidden="resetUploadModal"
    >
      <div v-if="!uploadResult">
        <div class="alert alert-info alert-sm mb-3">
          <strong>Instrucciones:</strong><br>
          1. Descargue la plantilla del mes usando el botón <em>Descargar plantilla</em>.<br>
          2. Complete las celdas en blanco con los siguientes códigos:
          <ul class="mb-0 mt-1">
            <li><strong>09:00-18:00</strong> — hora entrada-salida (ASISTIO)</li>
            <li><strong>F</strong> — Falta injustificada</li>
            <li><strong>FJ</strong> — Falta justificada</li>
            <li><strong>P</strong> — Permiso</li>
            <li><strong>FE</strong> — Feriado</li>
          </ul>
          Las celdas con D, V o DM ya están pre-marcadas y serán ignoradas.
        </div>
        <div class="form-group">
          <label class="label-form">Archivo Excel <span class="text-danger">*</span></label>
          <input
            type="file"
            class="form-control-file"
            accept=".xlsx,.xls"
            ref="fileInput"
            @change="onFileChange"
          />
          <div v-if="uploadSubmitted && !uploadFile" class="form-error mt-1">
            Seleccione un archivo
          </div>
        </div>
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="button button-light mr-2"
            @click="uploadModalVisible = false">Cancelar</button>
          <button type="button" class="button button-primary"
            :disabled="uploading" @click="processUpload">
            <span v-if="uploading"><i class="ri-loader-4-line"></i> Procesando...</span>
            <span v-else>Procesar</span>
          </button>
        </div>
      </div>

      <div v-else>
        <div :class="uploadResult.errors === 0 ? 'alert alert-success' : 'alert alert-warning'">
          <strong>{{ uploadResult.processed }}</strong> registro(s) procesados correctamente.
          <span v-if="uploadResult.errors > 0">
            <strong class="text-danger"> {{ uploadResult.errors }}</strong> error(es).
          </span>
        </div>
        <ul v-if="uploadResult.errorMessages && uploadResult.errorMessages.length" class="upload-errors small">
          <li v-for="(msg, i) in uploadResult.errorMessages" :key="i" class="text-danger">{{ msg }}</li>
        </ul>
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="button button-primary" @click="closeUploadResult">
            Cerrar
          </button>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { Alert, SearchableDropdown } from 'wize-admin'
import { AttendanceService, CompanyService, AreaService } from '@/core/services'
import { Constants } from '@/core/utils'
import AttendanceEditModal from './components/AttendanceEditModal.vue'

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]

const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S']

export default {
  name: 'AttendanceGrid',
  components: { SearchableDropdown, AttendanceEditModal },

  data() {
    const now = new Date()
    return {
      currentYear:  now.getFullYear(),
      currentMonth: now.getMonth(),
      rows:      [],
      companies: [],
      areas:     [],
      loading:   false,
      filters: {
        companyId: null,
        areaId:    null,
      },
      modalVisible: false,
      selectedCell: null,
      statusList: Object.values(Constants.AttendanceStatus),
      // Upload modal
      uploadModalVisible:  false,
      uploadFile:          null,
      uploading:           false,
      uploadSubmitted:     false,
      uploadResult:        null,
      downloadingTemplate: false,
    }
  },

  computed: {
    monthLabel() {
      return `${MONTH_NAMES[this.currentMonth]} ${this.currentYear}`
    },
    daysInMonth() {
      return new Date(this.currentYear, this.currentMonth + 1, 0).getDate()
    },
    dayNumbers() {
      return Array.from({ length: this.daysInMonth }, (_, i) => i + 1)
    },
    gridStyle() {
      return `grid-template-columns: 200px repeat(${this.daysInMonth}, 32px)`
    },
  },

  methods: {
    async loadData() {
      try {
        this.loading = true
        this.$store.dispatch('app/loading', true)
        const payload = {
          year:  this.currentYear,
          month: this.currentMonth + 1,
        }
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        if (this.filters.areaId)    payload.areaId    = this.filters.areaId
        const resp = await AttendanceService.getMonthGrid(payload)
        this.rows = resp.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar la grilla de asistencia')
      } finally {
        this.loading = false
        this.$store.dispatch('app/loading', false)
      }
    },

    prevMonth() {
      if (this.currentMonth === 0) { this.currentMonth = 11; this.currentYear-- }
      else this.currentMonth--
      this.loadData()
    },

    nextMonth() {
      if (this.currentMonth === 11) { this.currentMonth = 0; this.currentYear++ }
      else this.currentMonth++
      this.loadData()
    },

    isWeekend(day) {
      const dow = new Date(this.currentYear, this.currentMonth, day).getDay()
      return dow === 0 || dow === 6
    },

    isToday(day) {
      const now = new Date()
      return now.getFullYear() === this.currentYear &&
             now.getMonth()    === this.currentMonth &&
             now.getDate()     === day
    },

    dayLetter(day) {
      return DAY_LETTERS[new Date(this.currentYear, this.currentMonth, day).getDay()]
    },

    getDay(row, day) {
      return (row.days || []).find(d => {
        const date = new Date(d.date + 'T00:00:00')
        return date.getDate() === day
      }) || null
    },

    statusColor(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.color : '#e9ecef'
    },

    statusLetter(key) {
      const found = Object.values(Constants.AttendanceStatus).find(s => s.key === key)
      return found ? found.letter : '?'
    },

    cellTooltip(day) {
      if (!day) return ''
      const s = Object.values(Constants.AttendanceStatus).find(x => x.key === day.status)
      let tip = s ? s.label : day.status
      if (day.entryTime) tip += ` | Entrada: ${day.entryTime}`
      if (day.exitTime)  tip += ` | Salida: ${day.exitTime}`
      if (day.tardinessMinutes > 0) tip += ` | Tardanza: ${day.tardinessMinutes} min`
      return tip
    },

    openCell(row, day) {
      const dateStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      this.selectedCell = {
        employeeId:   row.employeeId,
        employeeName: row.employeeName,
        recordDate:   dateStr,
        day:          this.getDay(row, day),
      }
      this.modalVisible = true
    },

    onSaved() {
      this.modalVisible = false
      this.loadData()
    },

    onCompanySelected(id) {
      this.filters.companyId = id
      this.loadData()
    },

    onAreaSelected(id) {
      this.filters.areaId = id
      this.loadData()
    },

    clearFilters() {
      this.filters.companyId = null
      this.filters.areaId    = null
      this.loadData()
    },

    async downloadTemplate() {
      try {
        this.downloadingTemplate = true
        const payload = { year: this.currentYear, month: this.currentMonth + 1 }
        if (this.filters.companyId) payload.companyId = this.filters.companyId
        if (this.filters.areaId)    payload.areaId    = this.filters.areaId
        const resp = await AttendanceService.downloadTemplate(payload)
        const url  = URL.createObjectURL(new Blob([resp.data]))
        const a    = document.createElement('a')
        a.href     = url
        a.download = `plantilla-asistencia-${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}.xlsx`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar la plantilla')
      } finally {
        this.downloadingTemplate = false
      }
    },

    onFileChange(e) {
      this.uploadFile = e.target.files[0] || null
    },

    async processUpload() {
      this.uploadSubmitted = true
      if (!this.uploadFile) return
      try {
        this.uploading = true
        const formData = new FormData()
        formData.append('file', this.uploadFile)
        const resp = await AttendanceService.uploadExcel(
          formData, this.currentYear, this.currentMonth + 1)
        this.uploadResult = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al procesar el archivo')
      } finally {
        this.uploading = false
      }
    },

    closeUploadResult() {
      this.uploadModalVisible = false
      this.loadData()
    },

    resetUploadModal() {
      this.uploadFile      = null
      this.uploadSubmitted = false
      this.uploadResult    = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
  },

  async created() {
    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload     || []).map(a => ({ key: a.id, label: a.name }))
    } catch (e) {
      console.error('Error al cargar catálogos', e)
    }
    this.loadData()
  },
}
</script>

<style scoped lang="stylus">

/* ── Filtros ──────────────────────────────────────── */
.grid-filters
  .filter-field
    min-width 220px

/* ── Contenedor con scroll horizontal ───────────── */
.attendance-outer
  width 100%

.attendance-wrapper
  overflow-x auto
  border 1px solid #dee2e6
  border-radius 6px

/* ── Fila base ───────────────────────────────────── */
.att-row
  display grid
  border-bottom 1px solid #e9ecef

  &:last-child
    border-bottom none

/* ── Columna nombre (sticky) ─────────────────────── */
.att-col-name
  position sticky
  left 0
  background #fff
  z-index 3
  padding 4px 8px
  display flex
  flex-direction column
  justify-content center
  border-right 2px solid #dee2e6
  min-height 48px
  max-width 200px

  .emp-name
    font-size 12px
    font-weight 600
    white-space nowrap
    overflow hidden
    text-overflow ellipsis

  .emp-doc
    font-size 11px
    color #6c757d

/* ── Header ──────────────────────────────────────── */
.att-header
  background #f8f9fa
  border-bottom 2px solid #dee2e6
  position sticky
  top 0
  z-index 5

.att-header-name
  background #f8f9fa
  font-weight 700
  font-size 11px
  text-transform uppercase
  color #6c757d

.att-header-day
  display flex
  flex-direction column
  align-items center
  justify-content center
  border-right 1px solid #e9ecef
  min-height 44px
  background #f8f9fa
  cursor default

  .day-number
    font-size 11px
    font-weight 700
    color #495057
    line-height 1

  .day-letter
    font-size 10px
    color #adb5bd
    margin-top 2px

  &.weekend
    background #efefef
    .day-number
      color #adb5bd
    .day-letter
      color #ced4da

  &.today
    background #e3f2fd
    .day-number
      color #1976d2

/* ── Celda de día ────────────────────────────────── */
.att-day-cell
  display flex
  align-items center
  justify-content center
  border-right 1px solid #f0f0f0
  min-height 48px
  cursor pointer
  transition background 0.1s

  &:hover
    background #f8f9fa

  &.weekend
    background #fafafa

  &.today
    background #e8f4fd

/* ── Badge de status ─────────────────────────────── */
.att-cell-badge
  width 26px
  height 26px
  border-radius 4px
  display flex
  align-items center
  justify-content center
  font-size 9px
  font-weight 700
  color #fff
  user-select none
  transition box-shadow 0.1s

  &.auto-detected
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

  &.has-tardiness
    box-shadow 0 2px 0 0 #ffc107

.att-day-cell:hover .att-cell-badge
  box-shadow 0 0 0 2px rgba(0,0,0,0.2)

/* ── Navegación de mes ───────────────────────────── */
.month-nav
  .month-btn
    width 32px
    height 32px
    border 1px solid #dee2e6
    border-radius 4px
    background #fff
    cursor pointer
    display flex
    align-items center
    justify-content center
    font-size 18px
    color #495057
    padding 0
    transition background 0.1s

    &:hover
      background #f8f9fa

  .month-label
    font-size 16px
    font-weight 600
    min-width 160px
    text-align center
    color #212529

/* ── Leyenda ─────────────────────────────────────── */
.att-legend
  .legend-item
    display flex
    align-items center

  .legend-dot
    width 14px
    height 14px
    border-radius 3px
    display inline-block
    flex-shrink 0

  .tardiness-dot
    background #ffc107
    height 4px
    border-radius 2px

  .auto-dot
    background #adb5bd
    opacity 0.65
    border 1px dashed rgba(0,0,0,0.25)

/* ── Modal de carga ──────────────────────────────── */
.upload-errors
  max-height 200px
  overflow-y auto
  padding-left 1rem

.alert-sm
  font-size 13px
  padding 10px 14px
</style>
